import React, { useMemo } from 'react';
import ReactDOMServer from 'react-dom/server';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import { useBaseMetricRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { REVIEW_SENTIMENT_ENUM } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewSentiment/constants';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { GenericXAxisLabel } from 'src/components/BeaconV2Components/TopEntityChartV2/GenericXAxisLabel';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import TopEntityChartV2Loading from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2Loading';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import {
  useAppSelector,
  useComparisonTimePeriodShortDisplayName,
  useMainTimePeriod,
  useMainTimePeriodShortDisplayName
} from 'src/utils/Hooks';
import useGenericAdvancedSearch from 'src/utils/Hooks/useGenericAdvancedSearch';
import { saveBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { METRICTYPE } from 'src/utils/entityDefinitions';

const useKeywordCountPositiveReviewData = (reviewType: 'positive' | 'negative') => {
  const { startWeek, endWeek } = useMainTimePeriod();
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector((state) => {
    return {
      startWeek: state.comparisonTimePeriod.startWeek,
      endWeek: state.comparisonTimePeriod.endWeek
    };
  });

  const requestBuilder = useBaseMetricRequestBuilder({
    requestId: 'keywordCountByPositiveReviews',
    indexName: 'review-metrics',
    groupByFieldName: 'extractedKeywords.keyword',
    startWeekId: startWeek,
    endWeekId: endWeek,
    fields: [{ name: 'reviewSentiment' }]
  });

  const comparisonRequestBuilder = useBaseMetricRequestBuilder({
    requestId: 'keywordCountByPositiveReviews-comparison',
    indexName: 'review-metrics',
    groupByFieldName: 'extractedKeywords.keyword',
    startWeekId: comparisonStartWeek,
    endWeekId: comparisonEndWeek,
    fields: [{ name: 'reviewSentiment' }]
  });

  requestBuilder.setPageSize(100);
  comparisonRequestBuilder.setPageSize(100);

  const reviewSentimentId = reviewType === 'positive' ? REVIEW_SENTIMENT_ENUM.POSITIVE : REVIEW_SENTIMENT_ENUM.NEGATIVE;

  requestBuilder.addConditionTermFilter('reviewSentiment', 'must', [reviewSentimentId]);
  comparisonRequestBuilder.addConditionTermFilter('reviewSentiment', 'must', [reviewSentimentId]);

  requestBuilder.addSortField('reviewSentiment', 'reviewSentiment', 'value_count', false);
  comparisonRequestBuilder.addSortField('reviewSentiment', 'reviewSentiment', 'value_count', false);

  const requests = [comparisonRequestBuilder.build(), requestBuilder.build()];
  const { data: response, isLoading } = useGenericAdvancedSearch({
    requestId: 'sentimentByTopic-stackedColumn',
    requestBody: requests,
    queryKeys: [requests]
  });

  const data = useMemo(() => {
    if (response) {
      try {
        const processData = (rawData) => {
          return rawData?.aggregations?.['by_extractedKeywords.keyword'] ?? [];
        };

        const getFieldIds = (rawData) => {
          return rawData.map((datum) => datum.fieldId);
        };

        const mergeFieldIds = (primaryData, comparisonData) => {
          return Array.from(
            new Set([...getFieldIds(processData(primaryData)), ...getFieldIds(processData(comparisonData))])
          );
        };

        const generateChartData = (mergedCategoryData, rawData) => {
          return mergedCategoryData.map((datum) => {
            const matchedDatum = processData(rawData).find((categoryData) => categoryData.fieldId === datum);
            return matchedDatum ? [matchedDatum.fieldId, matchedDatum.value] : [datum, 0];
          });
        };

        const [primaryData, comparisonData] = response?.data ?? [];

        const categoryData = mergeFieldIds(primaryData, comparisonData);

        const chartData = generateChartData(categoryData, primaryData);

        const comparisonChartData = generateChartData(categoryData, comparisonData);

        return { chartData, comparisonChartData };
      } catch (e) {
        console.error(e);
        return null;
      }
    } else {
      return null;
    }
  }, [response]);

  return { data, isLoading };
};

const KeywordCountBySentimentTypeColumnChart = ({ reviewType }: { reviewType: 'positive' | 'negative' }) => {
  const { data, isLoading } = useKeywordCountPositiveReviewData(reviewType);
  const comparisonTimePeriodShortDisplayName = useComparisonTimePeriodShortDisplayName();
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const { displayName: mainTimePeriodDisplayName } = useMainTimePeriod();
  const { displayName: comparisonTimePeriodDisplayName } = useAppSelector((state) => state.comparisonTimePeriod);

  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();

  return (
    <BeaconChartWithLegend
      title={`Keyword Count in ${reviewType === 'positive' ? 'Positive' : 'Negative'} Reviews`}
      loading={isLoading}
      loadingComponent={<TopEntityChartV2Loading />}
      includeSummaryInfo={false}
      primaryLegendProps={{ displayName: mainTimePeriodDisplayName }}
      comparisonLegendProps={{ displayName: comparisonTimePeriodDisplayName }}
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">
            {`The chart displays the most frequently mentioned keywords in ${reviewType} reviews for ${entityPhrase} `}
            {taxonomyText}{' '}
            {`reviews during the
            ${mainTimePeriodShortDisplayName} period compared to the ${comparisonTimePeriodShortDisplayName}`}
            .
            <SubtitleFilters />
          </Text>
        </BeaconSubtitleContainer>
      }
      convertSeriesToDelimitedData={async () => {
        return saveBarChartAsDelimitedData(data.comparisonChartData, data.chartData);
      }}
    >
      <TopEntityChartV2
        xAxisLabelTextStyles={{ lineHeight: 1.6 }}
        xAxisFormatter={(xAxisCategory) => {
          return ReactDOMServer.renderToStaticMarkup(
            <GenericXAxisLabel xAxisCategory={xAxisCategory} wrapperStyle={{ width: '50px' }} />
          );
        }}
        chartSeries={[
          {
            type: 'column',
            color: '#adbdcc',
            data: data?.chartData ?? [],
            metricType: METRICTYPE.VOLUME
          },
          {
            type: 'column',
            data: data?.comparisonChartData ?? [],
            metricType: METRICTYPE.VOLUME
          }
        ]}
      />
    </BeaconChartWithLegend>
  );
};

export default KeywordCountBySentimentTypeColumnChart;
